/**
 * Evidence sub-agent citation model: its search tools return a versioned
 * `EvidenceAgentSearchPayloadV1` on `provider_metadata.data`, and its answer text
 * carries inline `<cite>TOKEN</cite>` markers. This module validates a tool-result
 * payload (`parseEvidenceAgentSearchPayload`), merges every payload of one child run
 * into a `CitationRegistry` (`buildCitationRegistry`), resolves a token to its source
 * + candidates (`resolveTokenSource`), maps the payloads' `image_id`s for `<img>` anchors
 * (`buildImageRegistry`), and normalizes a BE source into the chat
 * `Source` / `SimpleCitation` shapes the existing Evidence UI already renders
 * (`toChatSource` / `toSimpleCitation`).
 */

import { z } from 'zod';

import { parseSource } from '@/components/chat/utils/convertChatMessages';
import { logger } from '@/utils/logger/logger';
import type {
  PartnerFigure,
  RegionalRelevance,
  SimpleCitation,
  Source,
} from '@heidi/evidence/contracts';

// ── Wire schema (EvidenceAgentSearchPayloadV1) ──────────────────────────────

export const citationCandidateSchema = z.object({
  candidate_id: z.string(),
  source_id: z.string(),
  text: z.string(),
  provider_rank: z.number(),
  result_index: z.number(),
  // Kept as a plain string (not a zod enum) so a new BE strategy never fails the
  // whole payload parse; the value is display-only.
  strategy: z.string(),
});
export type CitationCandidateV1 = z.infer<typeof citationCandidateSchema>;

/**
 * Only `source_id` (the token join target) is load-bearing; other fields are
 * consumed defensively (`passthrough` preserves branding/display fields). The BE
 * keys each source by `id`, so normalize `id` → `source_id` before validating —
 * the rest of the model reads the single `source_id` join key.
 */
export const evidenceAgentSourceSchema = z.preprocess(
  (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const rec = raw as Record<string, unknown>;
      if (rec.source_id === undefined && typeof rec.id === 'string') {
        return { ...rec, source_id: rec.id };
      }
    }
    return raw;
  },
  z.object({ source_id: z.string() }).passthrough()
);
export type EvidenceAgentSource = z.infer<typeof evidenceAgentSourceSchema>;

// A malformed source must not void the rest of the array — validate elements
// individually and drop only the failures, preserving order.
const sourcesArraySchema = z
  .array(z.unknown())
  .default([])
  .transform((raw) =>
    raw
      .map((entry) => evidenceAgentSourceSchema.safeParse(entry))
      .filter(
        (result): result is { success: true; data: EvidenceAgentSource } => result.success
      )
      .map((result) => result.data)
  );

// Coerces null/empty/non-string to `undefined` so a sloppy optional field never
// voids an otherwise-valid image entry.
const optionalNonEmptyString = z.preprocess(
  (value) => (typeof value === 'string' && value.length > 0 ? value : undefined),
  z.string().optional()
);

const FIGURE_ID_RE = /^[0-9a-f]{64}$/;

/**
 * Tavily image (`https` url) XOR licensed partner figure (`figure_id`). Signed
 * asset URLs are never accepted — the FE blob-fetches figures by id.
 */
export const evidenceAgentImageSchema = z
  .object({
    url: z.preprocess(
      (value) =>
        typeof value === 'string' && value.startsWith('https://') ? value : undefined,
      z.string().optional()
    ),
    figure_id: z.preprocess(
      (value) =>
        typeof value === 'string' && FIGURE_ID_RE.test(value) ? value : undefined,
      z.string().regex(FIGURE_ID_RE).optional()
    ),
    title: optionalNonEmptyString,
    description: optionalNonEmptyString,
    // Anchor token the child can emit as `<img>TOKEN</img>`; absent on payloads
    // persisted before the BE started deriving it.
    image_id: optionalNonEmptyString,
  })
  .superRefine((image, ctx) => {
    const hasUrl = typeof image.url === 'string' && image.url.startsWith('https://');
    const hasFigure = typeof image.figure_id === 'string';
    if (hasUrl === hasFigure) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'exactly one of https url or figure_id is required',
      });
    }
  });
export type EvidenceAgentImage = z.infer<typeof evidenceAgentImageSchema>;

// Same resilience as sources: validate entries individually, drop only failures.
const imagesArraySchema = z
  .array(z.unknown())
  .default([])
  .transform((raw) =>
    raw
      .map((entry) => evidenceAgentImageSchema.safeParse(entry))
      .filter(
        (result): result is { success: true; data: EvidenceAgentImage } => result.success
      )
      .map((result) => result.data)
  );

export const evidenceAgentSearchPayloadSchema = z.object({
  // Pinned discriminator — a future breaking bump fails the parse and degrades to
  // "no payload" (generic row) instead of mis-rendering as v1.
  version: z.literal('1'),
  sources: sourcesArraySchema,
  citations: z.record(z.string()).default({}), // token -> source_id
  // Defaults to `{}` so payloads persisted before candidates existed still validate on reload.
  citation_candidates: z.record(z.array(citationCandidateSchema)).default({}),
  // Defaults to `[]` so payloads persisted before the typed images contract
  // still validate on reload.
  images: imagesArraySchema,
});
export type EvidenceAgentSearchPayloadV1 = z.infer<
  typeof evidenceAgentSearchPayloadSchema
>;

/**
 * Validate an unknown tool-result payload. Returns `null` unless it is a valid
 * V1 (so callers fall through to a generic row). Only warns when the value looks
 * like an intended-but-malformed V1 payload, to avoid noise on running tools
 * (no `data` yet) and unrelated results.
 */
export const parseEvidenceAgentSearchPayload = (
  data: unknown
): EvidenceAgentSearchPayloadV1 | null => {
  const result = evidenceAgentSearchPayloadSchema.safeParse(data);
  if (result.success) return result.data;
  if (data && typeof data === 'object' && 'version' in data) {
    // Log only structural fields — a raw `ZodIssue` carries `received` (the
    // failing runtime value), which could be clinical content in a healthcare
    // payload and must not reach Datadog.
    logger.warn('Invalid EvidenceAgentSearchPayloadV1', {
      issues: result.error.issues.map(({ path, code }) => ({ path, code })),
    });
  }
  return null;
};

// ── Registry ────────────────────────────────────────────────────────────────

export interface CitationRegistry {
  /** token -> source_id (conflicting tokens are removed, not overwritten). */
  tokenToSourceId: Map<string, string>;
  sourcesById: Map<string, EvidenceAgentSource>;
  /** source_id -> candidates, best-first, deduped by candidate_id. */
  candidatesBySourceId: Map<string, CitationCandidateV1[]>;
  /** Tokens rejected because they mapped to >1 source_id across the child's searches. */
  conflictingTokens: Set<string>;
}

/**
 * Merge every search payload of ONE child run. Never call across child runs —
 * tokens are only unique within a run's registry. Dedupes sources by
 * `source_id`, candidates by `candidate_id` (order preserved), and rejects a
 * token that maps to two source_ids.
 */
export const buildCitationRegistry = (
  payloads: readonly EvidenceAgentSearchPayloadV1[]
): CitationRegistry => {
  const tokenToSourceId = new Map<string, string>();
  const conflictingTokens = new Set<string>();
  const sourcesById = new Map<string, EvidenceAgentSource>();
  const candidatesBySourceId = new Map<string, CitationCandidateV1[]>();
  const seenCandidateIds = new Map<string, Set<string>>();

  for (const payload of payloads) {
    for (const source of payload.sources) {
      if (!sourcesById.has(source.source_id)) {
        sourcesById.set(source.source_id, source);
      }
    }

    for (const [sourceId, candidates] of Object.entries(payload.citation_candidates)) {
      const merged = candidatesBySourceId.get(sourceId) ?? [];
      const seen = seenCandidateIds.get(sourceId) ?? new Set<string>();
      for (const candidate of candidates) {
        // Integrity: a candidate must agree with the source_id it is keyed under.
        if (candidate.source_id !== sourceId) {
          logger.warn('Citation candidate source_id disagrees with its key', {
            key: sourceId,
            candidateId: candidate.candidate_id,
          });
          continue;
        }
        if (seen.has(candidate.candidate_id)) continue;
        seen.add(candidate.candidate_id);
        merged.push(candidate);
      }
      candidatesBySourceId.set(sourceId, merged);
      seenCandidateIds.set(sourceId, seen);
    }

    for (const [token, sourceId] of Object.entries(payload.citations)) {
      if (conflictingTokens.has(token)) continue;
      const existing = tokenToSourceId.get(token);
      if (existing !== undefined && existing !== sourceId) {
        logger.warn('Citation token maps to conflicting source_ids — rejecting', {
          token,
          existing,
          incoming: sourceId,
        });
        conflictingTokens.add(token);
        tokenToSourceId.delete(token);
        continue;
      }
      tokenToSourceId.set(token, sourceId);
    }
  }

  return { tokenToSourceId, sourcesById, candidatesBySourceId, conflictingTokens };
};

/**
 * Merge every search payload of ONE child run into an `image_id → image` map for
 * `<img>TOKEN</img>` anchors. Entries without an `image_id` are skipped (legacy
 * payloads — strip-only). The same id repeated with the SAME identity (url or
 * figure_id) is normal, so first entry wins; the same id with a DIFFERENT
 * identity is a contract error and poisons that id, because rendering the wrong
 * image against a clinical claim is worse than rendering none.
 */
export const buildImageRegistry = (
  payloads: readonly EvidenceAgentSearchPayloadV1[]
): Map<string, EvidenceAgentImage> => {
  const imagesById = new Map<string, EvidenceAgentImage>();
  const conflictingIds = new Set<string>();

  for (const payload of payloads) {
    for (const image of payload.images) {
      const imageId = image.image_id;
      if (!imageId || conflictingIds.has(imageId)) continue;
      const existing = imagesById.get(imageId);
      if (!existing) {
        imagesById.set(imageId, image);
        continue;
      }
      if (existing.url !== image.url || existing.figure_id !== image.figure_id) {
        logger.warn('Evidence image_id maps to conflicting identities — rejecting', {
          imageId,
        });
        conflictingIds.add(imageId);
        imagesById.delete(imageId);
      }
    }
  }

  return imagesById;
};

export interface ResolvedSource {
  sourceId: string;
  source: EvidenceAgentSource;
  /** Best-first candidates for this source; may be empty (pre-candidate payloads). */
  candidates: CitationCandidateV1[];
}

/**
 * Validate a token against the registry. Returns `null` for unknown, conflicting,
 * or dangling (token → source_id with no source) tokens — callers render those as
 * plain text, never a broken link.
 */
export const resolveTokenSource = (
  registry: CitationRegistry,
  token: string
): ResolvedSource | null => {
  if (registry.conflictingTokens.has(token)) return null;
  const sourceId = registry.tokenToSourceId.get(token);
  if (!sourceId) return null;
  const source = registry.sourcesById.get(sourceId);
  if (!source) return null;
  return {
    sourceId,
    source,
    candidates: registry.candidatesBySourceId.get(sourceId) ?? [],
  };
};

// ── Normalizers (BE source → chat shapes) ───────────────────────────────────

const SOURCE_TYPE_MAP: Record<string, NonNullable<SimpleCitation['source_type']>> = {
  web: 'web_search',
  url: 'web_search',
  web_search: 'web_search',
  user_doc: 'user_document',
  user_document: 'user_document',
  integration_partner: 'integration_partner',
  image: 'image',
  attachment: 'attachment',
};

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const strList = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === 'string')
    ? (value as string[])
    : undefined;

// Wire vocabulary of the BE `regional_relevance` field. Anything else (a
// malformed or future value) is dropped so the jurisdiction chip stays hidden
// rather than guessing — the same "omit, don't guess" contract the BE applies
// when it cannot resolve the user's country.
const REGIONAL_RELEVANCE_VALUES: ReadonlySet<string> = new Set<RegionalRelevance>([
  'in_region',
  'global',
  'out_of_region',
]);

const regionalRelevance = (value: unknown): RegionalRelevance | undefined =>
  typeof value === 'string' && REGIONAL_RELEVANCE_VALUES.has(value)
    ? (value as RegionalRelevance)
    : undefined;

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const partnerFigures = (value: unknown): PartnerFigure[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const figures: PartnerFigure[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const rec = entry as Record<string, unknown>;
    const id = str(rec.id);
    if (!id || !FIGURE_ID_RE.test(id)) continue;
    figures.push({
      id,
      ...(str(rec.label) && { label: rec.label as string }),
      ...(str(rec.caption) && { caption: rec.caption as string }),
    });
  }
  return figures.length > 0 ? figures : undefined;
};

const citationSourceType = (
  source: EvidenceAgentSource
): NonNullable<SimpleCitation['source_type']> => {
  const raw = str(source.type) ?? str(source.source_type);
  const mapped = raw ? SOURCE_TYPE_MAP[raw] : undefined;
  return mapped ?? 'web_search';
};

/**
 * BE source → chat `Source` for the search-card UI. Reuses `parseSource` for
 * the partner-branding rules, then re-attaches `source_id` (the web branch of
 * `parseSource` drops it) so the cards can dedupe / CPD-track.
 */
export const toChatSource = (source: EvidenceAgentSource): Source | null => {
  const parsed = parseSource(source, null, 0);
  if (!parsed) return null;
  if (!parsed.source_id) parsed.source_id = source.source_id;
  const domain = str(source.domain);
  if (!parsed.domain && domain) parsed.domain = domain;
  return parsed;
};

/**
 * BE source (+ the claim-selected candidate) → `SimpleCitation` for `CitationBadge`
 * and, after `enrichCitations`, the reference list. `citedText` comes from the
 * selected candidate (never `source.content[:500]`).
 */
export const toSimpleCitation = (
  source: EvidenceAgentSource,
  candidate?: CitationCandidateV1
): SimpleCitation => {
  const figures = partnerFigures(source.figures);
  return {
    title:
      str(source.title) ??
      str(source.display_name) ??
      str(source.domain) ??
      str(source.url) ??
      '',
    source_id: source.source_id,
    source_type: citationSourceType(source),
    ...(str(source.url) && { url: source.url as string }),
    ...(str(source.domain) && { domain: source.domain as string }),
    ...(str(source.display_name) && { display_name: source.display_name as string }),
    ...(str(source.website_name) && { website_name: source.website_name as string }),
    ...(str(source.document_id) && { document_id: source.document_id as string }),
    ...(str(source.partner_type) && { partner_type: source.partner_type as string }),
    ...(str(source.logo_url) && { logo_url: source.logo_url as string }),
    ...(str(source.brand_color) && { brand_color: source.brand_color as string }),
    ...(Array.isArray(source.collections) && {
      collections: source.collections as string[],
    }),
    ...(str(source.display_summary) && {
      display_summary: source.display_summary as string,
    }),
    // sources.json badge metadata (guideline / authority tier / jurisdiction chip).
    ...(strList(source.website_type) && {
      website_type: source.website_type as string[],
    }),
    ...(strList(source.regions) && { regions: source.regions as string[] }),
    ...(finiteNumber(source.authoritativeness_score) !== undefined && {
      authoritativeness_score: source.authoritativeness_score as number,
    }),
    ...(regionalRelevance(source.regional_relevance) && {
      regional_relevance: source.regional_relevance as RegionalRelevance,
    }),
    ...(figures && { figures }),
    ...(candidate && { citedText: candidate.text }),
  };
};
